// mirror of auware/.../frontend/src/lib/api.ts (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations: refresh cookie name is `buyist_support_rt`
// (handled server-side); endpoint paths and behavior are otherwise
// identical to the Auware mutex-refresh implementation.

import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { useAuthStore } from "@/stores/auth";

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

/**
 * Pre-configured Axios instance used for every API call in the app.
 *
 * - Base URL is `/api/v1` which Vite proxies to the FastAPI backend in dev.
 * - In production Caddy / NGINX routes `/api` to the backend.
 * - A request interceptor attaches the JWT access token to every request.
 * - A response interceptor silently refreshes expired access tokens using
 *   the refresh cookie, and only logs the user out if the refresh itself
 *   fails.
 */
const api = axios.create({
  baseURL: "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
  // Send cookies on every request so the HttpOnly `buyist_support_rt`
  // refresh cookie flows on `/auth/refresh` calls. Same-origin by
  // default so no CORS implications in prod.
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Request interceptor - attach JWT
// ---------------------------------------------------------------------------

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { tokens } = useAuthStore.getState();

    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor - silent refresh on 401, then retry
// ---------------------------------------------------------------------------

/**
 * Mutex for the refresh flow. When multiple requests fail with 401 at
 * the same time (common since the access token expires for ALL requests
 * at once), only ONE refresh call is made. The others queue up and
 * retry once the refresh resolves.
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeToRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshComplete(newAccessToken: string) {
  refreshSubscribers.forEach((cb) => cb(newAccessToken));
  refreshSubscribers = [];
}

function onRefreshFailed() {
  refreshSubscribers.forEach((cb) => cb(""));
  refreshSubscribers = [];
}

api.interceptors.response.use(
  // Happy path - pass through unchanged.
  (response) => response,

  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401s
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Skip auth-related endpoints (login, refresh, password-setup) - a
    // 401 there is an expected "wrong credentials" error, not a session
    // issue.
    const url = originalRequest.url ?? "";
    if (url.includes("/auth/")) {
      return Promise.reject(error);
    }

    // Don't retry a request we already retried (prevents infinite loops)
    if (originalRequest._retry) {
      useAuthStore.getState().logout();
      return Promise.reject(error);
    }

    // Only act if the user has an active session. Stale 401s from
    // pre-login requests must NOT trigger a refresh.
    const requestAuth = originalRequest.headers?.Authorization;
    const currentToken = useAuthStore.getState().tokens?.accessToken;
    if (!currentToken) {
      return Promise.reject(error);
    }

    // If the token was already refreshed by another 401 handler while
    // this request was in-flight, retry immediately with the new token.
    if (requestAuth !== `Bearer ${currentToken}`) {
      originalRequest._retry = true;
      originalRequest.headers.Authorization = `Bearer ${currentToken}`;
      return api(originalRequest);
    }

    // If a refresh is already in progress, queue this request.
    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        subscribeToRefresh((newToken: string) => {
          if (!newToken) {
            reject(error);
            return;
          }
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    // --- Attempt silent refresh ---
    isRefreshing = true;
    originalRequest._retry = true;

    const inMemoryRefresh = useAuthStore.getState().tokens?.refreshToken;
    try {
      // Use raw axios (not our `api` instance) to avoid interceptor loops.
      // `withCredentials: true` ensures the `buyist_support_rt` cookie
      // rides along.
      const { data } = await axios.post(
        "/api/v1/auth/refresh",
        inMemoryRefresh ? { refresh_token: inMemoryRefresh } : {},
        { withCredentials: true },
      );

      const newAccessToken: string = data.access_token;
      const newRefreshToken: string = data.refresh_token;

      useAuthStore.getState().setTokens({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
      });

      isRefreshing = false;
      onRefreshComplete(newAccessToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshFailed();
      // The cookie is already stale or revoked server-side - avoid a
      // redundant `/auth/logout` call. Just clear local state.
      useAuthStore.getState().logout({ serverSide: false });
      return Promise.reject(refreshError);
    }
  },
);

export default api;
